import React from 'react';
import {
    Box,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Folder } from 'react-feather';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from "react-redux";
import { Form, Label, Input } from 'reactstrap';
import AgreementModal from '../../components/AgreementModal';
import PlanNamingFields from '../../components/PlanNamingFields';
import LoadingReport from './Loading';
import { buildPlanFileName, normalizePlanNameSegment } from '../../utils/planNamingUtils';
import { useFileCount, usePointCountPost, useFetchPolicyPdf } from '../../hooks/useFiles';
import { useUpdateAgreement } from '../../hooks/useAuth';

function Upload(props) {
    /// File upload for plans to be processed
    const dispatch = useDispatch();
    const pointCountPost = usePointCountPost();
    const fetchPolicyPdf = useFetchPolicyPdf();
    const updateAgreement = useUpdateAgreement();
    const customerSelect = useSelector((state) => state.UserReducer.customerSelection);
    const userdata = useSelector((state)=> state.UserReducer.user);
    const company = useSelector((state) => state.CompanyReducer.userCompanies[0]);
    const filePoints = useSelector((state) => state.FileReducer.filePoints);
    const [selectFilePoints, setSelectFilePoints] = React.useState([]);
    const { data: fileStorageCount = 0 } = useFileCount({
        companyId: company?.id,
        enabled: Boolean(company?.id),
    });

    const [pdfStatus, setPdfStatus] = React.useState();
    const { setDFile,
        setJobName, setAreaName, setFloorName, setFile, handleUpload, noProcess, setNoProcess,
        setFileName, pdfUrl, setPdfUrl } = props;
    const { acceptedFiles, getRootProps, getInputProps } = useDropzone();

    const [formJobName, setFormJobName] =  React.useState('');
    const [formAreaName, setFormAreaName] =  React.useState('');
    const [formFloorName, setFormFloorName] =  React.useState('');
    const [showAgreementModal, setShowAgreementModal] = React.useState(true);

    React.useEffect(() => {
        const effectiveJobName = normalizePlanNameSegment(formJobName);
        const effectiveAreaName = normalizePlanNameSegment(formAreaName);
        const effectiveFloorName = normalizePlanNameSegment(formFloorName);

        setJobName(effectiveJobName);
        setAreaName(effectiveAreaName);
        setFloorName(effectiveFloorName);

        const built = buildPlanFileName({
            jobName: formJobName,
            floorName: formFloorName,
            areaName: formAreaName,
        });
        if (built) {
            setFileName(built);
            setDFile(false);
        } else {
            setFileName('');
            setDFile(true);
        }
    }, [formJobName, formAreaName, formFloorName]);

    let user = {
        Id: userdata.id,
        hasAcceptedUploadPolicy: userdata.hasAcceptedUploadPolicy,
        hasAcceptedDownloadPolicy: userdata.hasAcceptedDownloadPolicy,
        hasConfirmedEmail: userdata.hasConfirmedEmail,
        hasReadPrivacyPolicy: userdata.hasReadPrivacyPolicy,
    };

    function backToUpload(){
        dispatch({ type: 'SET_FILE_POINTS', payload: []});
        setFileName('');
        setFormJobName('');
        setJobName('');
        setFormAreaName('');
        setAreaName('');
        setFormFloorName('');
        setFloorName('');
    }

    let pointCount = 0;
    let ecuPoints = 0;
    if (filePoints?.pointList?.length > 0){
        for (let p = 0; p < filePoints?.pointList?.length; p++){
            pointCount += filePoints?.pointList[p]?.quantity;
            if (filePoints?.pointList[p]?.typeEnum == 1 || filePoints?.pointList[p]?.typeEnum == 2 || filePoints?.pointList[p]?.typeEnum == 3){
                ecuPoints += filePoints?.pointList[p]?.quantity;
            }
        }
    }

    React.useEffect(()=>{
        setFile([]);
    },[]);

    React.useEffect(() => {
        fetchPolicyPdf.mutate({ pdfType: 1, setPdfUrl });
        setFile(acceptedFiles);
        if (userdata.hasAcceptedUploadPolicy === 0){
            setPdfStatus(false)
        }
        if (userdata.hasAcceptedUploadPolicy === 1){
            setPdfStatus(true)
        }
        setSelectFilePoints(filePoints.pointList);
    },[acceptedFiles, filePoints],);

    const files = acceptedFiles.map(file => (
        <Tooltip key={file.path} title="Click to select different file">
            <ListItem sx={{ border: '1px solid black' }}>
                    <ListItemIcon>
                        <Folder/>
                    </ListItemIcon>
                    <ListItemText>
                        {file.path} - {file.size} bytes
                    </ListItemText>
            </ListItem>
        </Tooltip>
    ));

    /// Update user information once user has accepted download agreement
    function changePDFStatus(event){
        setPdfStatus(!pdfStatus);
        if (event === true) {
            user = {
                Id: userdata.id,
                hasAcceptedUploadPolicy: 1,
                hasAcceptedDownloadPolicy: userdata.hasAcceptedDownloadPolicy,
                hasConfirmedEmail: userdata.hasConfirmedEmail,
                hasReadPrivacyPolicy: userdata.hasReadPrivacyPolicy,
            }
            updateAgreement.mutate(user);
        }
        else if (event === false) {
            user = {
                Id: userdata.id,
                hasAcceptedUploadPolicy: 0,
                hasAcceptedDownloadPolicy: userdata.hasAcceptedDownloadPolicy,
                hasConfirmedEmail: userdata.hasConfirmedEmail,
                hasReadPrivacyPolicy: userdata.hasReadPrivacyPolicy,
            }
            updateAgreement.mutate(user);
        }
    };

    function setPointSelection(item){
        for (let p = 0; p < filePoints?.pointList.length; p++){
            if (item.description == filePoints?.pointList[p].description){
                filePoints.pointList[p].enabled = !(filePoints.pointList[p].enabled);
                setSelectFilePoints([...selectFilePoints]);
            }
        };
    };

    let fileToSend = {
        companyId: filePoints.companyId,
        ext: filePoints.ext,
        filePath1: filePoints.filePath1,
        id: filePoints.id,
        name: filePoints.name,
        newTxtName: filePoints.newTxtName,
        companionZipPath: filePoints.companionZipPath,
        hasCompanionFiles: filePoints.hasCompanionFiles,
        pointList: selectFilePoints,
        ziptmp: filePoints.ziptmp,
        customerId: customerSelect.userCompanyId
    }

    async function finishFileUpload(){
        if (selectFilePoints.length == 0){
            swal({
                title: `No Point Count Found.`,
                position: 'center'
            });
        } else {
            try {
                const result = await pointCountPost.mutateAsync(fileToSend);
                dispatch({ type: 'SET_FILE_POINTS', payload: [] });
                dispatch({ type: 'SUMMARY_FILE', payload: result.summary });
                dispatch({ type: 'VALIDATION_FILE', payload: result.validation });
                dispatch({ type: 'VALIDATION_ERROR', payload: result.sequencingErrorCode });
                dispatch({ type: 'VALIDATION_INFO', payload: result.fileInfo });
            } catch (error) {
                console.log(error);
            }
        }
    };

    if (pointCountPost.isLoading) {
        return <LoadingReport />;
    }

    return (
        <Box sx={{ width: '100%', flex: 1, margin: 0, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', p: 1 }}>
            { userdata.hasAcceptedUploadPolicy === 0 ? (
                showAgreementModal ? (
                    <AgreementModal
                        open={true}
                        onClose={() => setShowAgreementModal(false)}
                        title="Uploader Agreement"
                        description="Read and accept the below User Agreement to upload plans."
                        pdfUrl={pdfUrl}
                        checked={!!pdfStatus}
                        onToggle={changePDFStatus}
                        acceptLabel="Accept Upload Policy"
                    />
                ) : (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <p>You must accept the Uploader Agreement to upload plans.</p>
                        <Button variant="outlined" onClick={() => setShowAgreementModal(true)}>
                            View agreement
                        </Button>
                    </Box>
                )
            ) : (
            <div>
                {fileStorageCount < 100 ?
                <div>
                { filePoints.length == 0 ?
                <div>
                    <h2>Upload File</h2>
                    <label>Enter a custom <b>File Name</b>. Leave blank for <b>Default</b> file name generation.</label>
                    <br/>
                    <Box style={{margin:10}}>
                        <PlanNamingFields
                            jobName={formJobName}
                            floorName={formFloorName}
                            areaName={formAreaName}
                            onJobNameChange={setFormJobName}
                            onFloorNameChange={setFormFloorName}
                            onAreaNameChange={setFormAreaName}
                            disabled={company === undefined}
                            layout="inline"
                        />
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        flexDirection: 'column'
                    }}>
                        <Box
                            sx={{
                                margin: 5,
                                width: '80%',
                                height: '70%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderWidth: '2px',
                                borderRadius: '2px',
                                borderStyle: 'dashed',
                                padding: '20px',
                                transition: 'border .24s ease-in-out'
                            }}
                            {...getRootProps({className: 'dropzone'})}
                        >
                            <input disabled={company === undefined} {...getInputProps()}/>
                            { acceptedFiles.length > 0 ? <List>{files}</List> :
                            <p>
                                {noProcess
                                    ? 'Upload any file (up to 500 MB). Zipped or unzipped.'
                                    : <>For processing: Drag &apos;n&apos; drop a .txt file or a .zip containing a .txt plan plus any supporting files<br/>Maximum upload size: 500 MB.</>}
                            </p> }
                        </Box>
                    </Box>
                    <br/>
                    {company?.processExternal == true &&
                        <Box sx={{ margin: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <Tooltip
                                title="Upload a plan that does not go through Botmark processing, while still using estimates and file delivery. A minimum Botmark service fee still applies."
                                placement="top"
                                arrow
                            >
                                <Box
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                                >
                                    <Label check style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0 }}>
                                        <Typography component="span" fontWeight="bold">
                                            Upload Plan Without Processing File
                                        </Typography>
                                        <Input
                                            type="switch"
                                            role="switch"
                                            checked={noProcess}
                                            onChange={(e) => setNoProcess(e.target.checked)}
                                        />
                                    </Label>
                                </Box>
                            </Tooltip>
                        </Box>
                    }
                    <br/>
                    <Button sx={{ width: '50%' }} disabled={company === undefined} variant="contained" onClick={handleUpload} >Upload</Button>
                </div>
                :
                <div>
                    <h3>Points</h3>
                    <p>Point Count List: All detected points</p>
                    <p>Selected special points will be included in plan processing. Deselected points will be excluded.</p>
                    <p>Plan Point Total: {pointCount}</p>
                    <p>E+C+U Point Total: {ecuPoints}</p>
                    <Form>
                    {filePoints.pointList.map((item) => (
                        <div>
                        { item.typeEnum == 4 ?
                            <div style={{margin:5}}>
                                <Label check>
                                    <b>{item.description}</b> #: {item.quantity}
                                    <Input type="switch" role="switch" checked={item?.enabled}
                                        value={item?.description}
                                        onChange={() => setPointSelection(item)}
                                    />
                                </Label>
                            </div>
                            :
                            <div style={{margin:5}}>
                                <Label check>
                                    <b>{item.description}</b> #: {item.quantity}
                                </Label>
                            </div>
                        }
                        </div>
                    ))}
                    { pointCount > 0 ?
                        <div>
                            <Button onClick={()=> backToUpload()}>Reset & Back To Upload</Button>
                            <Button onClick={()=> finishFileUpload()}>Submit Point Selection</Button>
                        </div>
                        :
                        <div>
                            <Button onClick={()=> backToUpload()}>Reset & Back To Upload</Button>
                            <span>No Points found in File. Upload a File Containing Points.</span>
                        </div>
                    }
                    </Form>
                </div>
                }
                </div>
                :
                <div>
                    File Storage Capacity Reached: Please remove some files before uploading any new files to the system.
                </div>
            }
            </div>
            )}
        </Box>
    )
}

export default Upload;